/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import SnackbarMessage from '@ion/dls/src/components/snackbars/SnackbarMessage';
import useSnackbar from '@ion/dls/src/components/snackbars/useSnackbar';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import Hidden from '@ion/dls/src/layout/Hidden';
import {ClearSelectedNotificationsDialog} from '@ion/dls/src/notifications/ClearSelectedNotificationsDialog';
import {
  NotificationDrawer,
  NotificationTabType
} from '@ion/dls/src/notifications/NotificationDrawer';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {NotificationPanelTabs} from '@ion/dls/src/types/types';
import {reviewNotification} from '@/store/actions/tracking';
import {hasAppNotificationAccess} from '../../security/Features';
import {
  deselectAllNotifications,
  markAllAsCleared,
  markAllAsRead,
  selectAllNotifications,
  setNotificationsActivePanel,
  setNotificationsPanelStatus
} from '../../store/actions/notifications';
import {NotificationMenu} from '../notifications/NotificationMenu';
import NotificationPanelContent from '../notifications/NotificationPanelContent';

export interface NotificationPanelType {
  cancelToastDismiss: any[];
  setCancelToastDismiss: Function;
}

export const NotificationPanel = ({
  cancelToastDismiss,
  setCancelToastDismiss
}: NotificationPanelType) => {
  const canAccessNotification       = hasAppNotificationAccess();
  const {isCurrent: isCurrentTheme} = useThemeContext();
  const dispatch                    = useDispatch();
  const notificationPanelOpen       = useSelector(({notifications: {panel}}) => panel.open);
  const activeNotificationTab       = useSelector(({notifications: {panel}}) => panel.activePanel);
  const selectedNotifications       = useSelector(({notifications: {panel}}) => panel.selected);
  const allNotifications            = useSelector(({notifications}) => notifications.all);
  const urgentNotifications         = useSelector(({notifications}) => notifications.urgent);

  const [checkboxesEnabled, setCheckboxesEnabled]         = useState(false);
  const [isRead, setMarkAsRead]                           = useState(false);
  const [filterAll, setFilterAll]                         = useState(true);
  const [filterUnread, setFilterUnread]                   = useState(false);
  const [clearAllVerification, setClearAllVerification]   = useState(false);
  const [notificationMenuOpen, setNotificationMenuOpen]   = useState(false);
  const [notificationsCheckAll, setNotificationsCheckAll] = useState(false);
  const {isDesktop, isPhone}                              = useMedia();
  const isMounted                                         = useHasMounted();

  const {
    show: showClearSnackbar,
    message: clearSnackbarMessage,
    openSnackbar: openClearSnackbar,
    dismissSnackbar: dismissClearSnackbar
  } = useSnackbar();

  const [
    currentSelectedNotifications,
    setCurrentSelectedNotifications
  ] = useState([]);

  useEffect(() => {
    const currentNotifications = activeNotificationTab ? allNotifications : urgentNotifications;
    setNotificationsCheckAll(currentNotifications?.length === currentSelectedNotifications?.length);
  }, [
    currentSelectedNotifications,
    activeNotificationTab,
    allNotifications,
    urgentNotifications
  ]);

  const tabItems: NotificationTabType[] = [
    {
      tab       : NotificationPanelTabs.urgent,
      tabHeader : translate('notifications:panel.panelTabs.urgent')
    },
    {
      tab       : NotificationPanelTabs.allActivity,
      tabHeader : translate('notifications:panel.panelTabs.allActivity')
    }
  ];

  useEffect(() => {
    if (isMounted) {
      const urgentNotificationsLoaded = Array.isArray(urgentNotifications);
      if (!urgentNotifications?.length && urgentNotificationsLoaded) {
        onNotificationTabChange(tabItems[1]);
      } else if (!activeNotificationTab) {
        onNotificationTabChange(tabItems[0]);
      }
    }
  }, [urgentNotifications]);

  function closeNotifications() {
    dispatch(setNotificationsPanelStatus(false));
  }

  function onNotificationTabChange(tab: NotificationTabType) {
    dispatch(setNotificationsActivePanel(tab.tab));
  }

  function onReviewNotification(notification) {
    setCancelToastDismiss([...cancelToastDismiss, notification?.uuid]);
    if (isCurrentTheme || isPhone) {
      dispatch(setNotificationsPanelStatus(false));
    }
    dispatch(reviewNotification(notification) as any);
  }

  function onSelectMultiple() {
    setCheckboxesEnabled(!checkboxesEnabled);
    setNotificationMenuOpen(false);
  }

  function onMarkAllAsRead() {
    dispatch(markAllAsRead(allNotifications) as any);
    setNotificationMenuOpen(false);
  }

  function onSeeUnreadOnly() {
    setFilterAll(false);
    setFilterUnread(!filterUnread);
    setNotificationMenuOpen(false);
  }

  function onMarkSelectedAsRead() {
    dispatch(markAllAsRead(selectedNotifications) as any);
    setMarkAsRead(true);
    setNotificationMenuOpen(false);
  }

  function onClearSelected() {
    setClearAllVerification(true);
  }

  const clearAllSelectedNotifications = useCallback(function() {
    dispatch(markAllAsCleared(selectedNotifications) as any);
    setCheckboxesEnabled(false);
    setClearAllVerification(false);
    setNotificationMenuOpen(false);
    openClearSnackbar(translate('notifications:dialog.clearedSnackbar'));
  }, [dispatch, selectedNotifications, openClearSnackbar]);

  const onBulkDeleteSelected = isCurrentTheme ? clearAllSelectedNotifications : onClearSelected;

  // stable identity so ClearSelectedNotificationsDialog's own React.memo can
  // actually skip a re-render when its other props haven't changed ...
  const onDismissClearAllVerification = useCallback(function() {
    setClearAllVerification(false);
  }, []);

  function handleNotificationSelectAll(e) {
    if (!notificationsCheckAll) {
      dispatch(selectAllNotifications() as any);
    } else {
      dispatch(deselectAllNotifications() as any);
    }
    setNotificationsCheckAll(!notificationsCheckAll);
  }

  function onClearAll() {
    dispatch(selectAllNotifications() as any);
    setNotificationMenuOpen(false);
    setNotificationsCheckAll(true);
    setClearAllVerification(true);
  }

  function onSeeAll() {
    setFilterAll(true);
    setFilterUnread(false);
    setNotificationMenuOpen(false);
  }

  if (!canAccessNotification) {
    return null;
  }

  return (
    <>
      <Hidden show='desktop'>
        <NotificationMenu checkboxesEnabled={checkboxesEnabled}
          onSelectMultiple={onSelectMultiple}
          onMarkAllAsRead={onMarkAllAsRead}
          onClearAll={onClearAll}
          onSeeUnreadOnly={onSeeUnreadOnly}
          onMarkSelectedAsRead={onMarkSelectedAsRead}
          onClearSelected={onClearSelected}
          filterUnread={filterUnread}>
          <NotificationDrawer panelOpen={notificationPanelOpen}
            closeDrawer={closeNotifications}
            onTabChange={onNotificationTabChange}
            tabItems={tabItems}
            onSeeAll={onSeeAll}
            filterAll={filterAll}
            filterUnread={filterUnread}
            onSeeUnreadOnly={onSeeUnreadOnly}
            activeTab={activeNotificationTab}
            notificationMenuOpen={notificationMenuOpen}
            setNotificationMenuOpen={setNotificationMenuOpen}
            checkboxesEnabled={checkboxesEnabled}
            allNotifications={allNotifications}
            urgentNotifications={urgentNotifications}
            selectedNotifications={currentSelectedNotifications}
            onMarkSelectedAsRead={onMarkSelectedAsRead}
            onClearSelected={onBulkDeleteSelected}
            handleSelectAll={handleNotificationSelectAll}
            notificationsCheckAll={notificationsCheckAll}>
            <NotificationPanelContent checkboxesEnabled={isRead}
              filterUnread={filterUnread}
              isCheckAll={notificationsCheckAll}
              onReviewClick={onReviewNotification}
              currentSelectedNotifications={currentSelectedNotifications}
              setCurrentSelectedNotifications={setCurrentSelectedNotifications} />
          </NotificationDrawer>
        </NotificationMenu>
      </Hidden>
      <Hidden show='mobile'>
        <NotificationMenu checkboxesEnabled={checkboxesEnabled}
          onSelectMultiple={onSelectMultiple}
          onMarkAllAsRead={onMarkAllAsRead}
          onClearAll={onClearAll}
          onSeeUnreadOnly={onSeeUnreadOnly}
          onMarkSelectedAsRead={onMarkSelectedAsRead}
          onClearSelected={onClearSelected}
          filterUnread={filterUnread}>
          <NotificationDrawer panelOpen={notificationPanelOpen}
            closeDrawer={closeNotifications}
            onTabChange={onNotificationTabChange}
            tabItems={tabItems}
            activeTab={activeNotificationTab}
            notificationMenuOpen={notificationMenuOpen}
            setNotificationMenuOpen={setNotificationMenuOpen}
            checkboxesEnabled={checkboxesEnabled}
            selectedNotifications={currentSelectedNotifications}
            onMarkSelectedAsRead={onMarkSelectedAsRead}
            onClearSelected={onBulkDeleteSelected}
            handleSelectAll={handleNotificationSelectAll}
            notificationsCheckAll={notificationsCheckAll}>
            <NotificationPanelContent checkboxesEnabled={isRead}
              filterUnread={filterUnread}
              isCheckAll={notificationsCheckAll}
              onReviewClick={onReviewNotification}
              currentSelectedNotifications={currentSelectedNotifications}
              setCurrentSelectedNotifications={setCurrentSelectedNotifications} />
          </NotificationDrawer>
        </NotificationMenu>
      </Hidden>
      {isCurrentTheme ? (
        <>
          <ClearSelectedNotificationsDialog show={clearAllVerification}
            isCheckAll={notificationsCheckAll}
            isDesktop={isDesktop}
            isPhone={isPhone}
            onAction={clearAllSelectedNotifications}
            onDismiss={onDismissClearAllVerification} />
          <SnackbarMessage show={showClearSnackbar}
            message={clearSnackbarMessage}
            onDismiss={dismissClearSnackbar} />
        </>
      ) : (
        <UserNotificationDialog show={clearAllVerification}
          title={translate(`notifications:dialog.${notificationsCheckAll ? 'clearAll' : 'clearSelected'}`)}
          message={isDesktop ? translate(`notifications:dialog.${notificationsCheckAll ? 'allClearConfirmation' : 'clearConfirmation'}`)
            : translate('notifications:dialog.confirmation')}
          actionLabel={translate('notifications:dialog.yesClearAll')}
          onAction={clearAllSelectedNotifications}
          dismissLabel={translate('notifications:dialog.neverMind')}
          onDismiss={onDismissClearAllVerification}
          capitalize />
      )}
    </>
  );
};

export default NotificationPanel;
